/**
 * Main entry point - Unified Component Registry
 */
import { registerAllComponents } from "@/blocks";
import { LoadingImage } from "@/components/LoadingImage";
import { componentRegistry } from "@/utils/block-registry.util";
import { devLogger } from "@/utils/dev-logger.util";
import { initScrollAnimation } from "@/utils/scroll-animation.util";
// Global LoadingImage instance
function initializeLoadingImage() {
    new LoadingImage();
}
/**
 * Initialize components within a specific container
 * Can be called after dynamic content is loaded
 */
export async function initializeComponentsInContainer(container = document) {
    const elements = container.querySelectorAll("[data-component]");
    let totalInitialized = 0;
    if (elements.length === 0) {
        return 0;
    }
    const promises = [];
    elements.forEach((element) => {
        // Skip if already initialized
        if (element.hasAttribute("data-component-initialized")) {
            return;
        }
        const componentNames = element.getAttribute("data-component");
        if (!componentNames)
            return;
        const components = componentNames
            .split(",")
            .map((name) => name.trim())
            .filter(Boolean);
        components.forEach((componentName) => {
            const initPromise = componentRegistry.get(componentName).then((component) => {
                if (component) {
                    const componentKey = componentName.toLowerCase();
                    const specificPropsAttr = element.getAttribute(`data-props-${componentKey}`);
                    const propsAttr = specificPropsAttr || element.getAttribute("data-props");
                    let props;
                    if (propsAttr) {
                        try {
                            props = JSON.parse(propsAttr);
                        }
                        catch (e) {
                            devLogger.error(`Failed to parse props for ${componentName}:`, e);
                        }
                    }
                    component(element, props);
                    totalInitialized++;
                }
                else {
                    devLogger.warn(`⚠️ Component "${componentName}" not found in registry`);
                }
            });
            promises.push(initPromise);
        });
        // Mark as initialized to prevent double initialization
        element.setAttribute("data-component-initialized", "true");
    });
    await Promise.all(promises);
    return totalInitialized;
}
function initializeComponents() {
    registerAllComponents();
    initializeComponentsInContainer(document);
}
async function initializeApp() {
    initScrollAnimation();
    initializeLoadingImage();
    initializeComponents();
}
initializeApp().catch((error) => {
    devLogger.error("Failed to initialize app:", error);
});
/**
 * Export registries and utilities for manual initialization (for AJAX/dynamic content scenarios)
 */
export { registerAllComponents } from "@/blocks";
export { componentRegistry } from "@/utils/block-registry.util";
